import React from 'react';
import { BaseModal, BaseModalProps } from '@tcl-argon-foundations/base-modal';
import { InputTheme } from '@tcl-argon-models/models';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { Button } from '@tcl-argon-prefabs/button';
import { useModalStyles } from './styles';

export interface ModalProps extends Omit<BaseModalProps, 'contentLabel'> {
  /** The descriptive title of this modal. */
  title: string;
  /** Tempus modals come in 3 standard widths, represented here as 'small', 'medium', and 'large'. */
  size: 'small' | 'medium' | 'large';
  /**
   * A listener called when the user clicks the 'Confirm' button.
   * If no handler is provided, the button will not be shown.
   */
  onClickConfirm?: () => void;
  /**
   * A listener called when the user clicks the 'Cancel' button.
   * If no handler is provided, the button will not be shown.
   */
  onClickCancel?: () => void;
}

export const Modal: React.FC<ModalProps> = ({
  children,
  classes: classOverrides,
  className,
  onClickCancel,
  onClickConfirm,
  size,
  title,
  ...props
}) => {
  const classes = mergeClasses(useModalStyles({ size, title, isOpen: props.isOpen }), classOverrides, {
    root: className as string,
  });

  return (
    <BaseModal contentLabel={title} classes={{ root: classes.root }} data-analytics="modal" {...props}>
      <h1 className={classes.title}>{title}</h1>
      <div className={classes.content}>{children}</div>
      <div className={classes.buttons}>
        {onClickCancel && (
          <Button theme={InputTheme.Default} onClick={onClickCancel}>
            Cancel
          </Button>
        )}
        {onClickConfirm && <Button onClick={onClickConfirm}>Confirm</Button>}
      </div>
    </BaseModal>
  );
};
